<template>
  <CRow class="form-group material-katalog-auswahl-formgroup">
    <CCol sm="12">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
        <label :class="[formLabelClass, 'mb-0']">{{ formLabel }}</label>
        <div class="d-flex flex-wrap align-items-center justify-content-end">
          <CButton
            color="light"
            size="sm"
            class="material-filter-toggle mr-2"
            :class="{ 'material-filter-toggle--active': numbersOnly }"
            @click="toggleNumbersOnly"
          >
            <span class="material-filter-toggle__state" :class="{ 'material-filter-toggle__state--active': numbersOnly }">
              <CIcon :name="numbersOnly ? 'cil-check' : 'cil-x'" />
            </span>
            <span class="material-filter-toggle__text">
              <span class="material-filter-toggle__title">Nur Nummern</span>
              <span class="material-filter-toggle__hint">
                {{ numbersOnly ? 'Nur Artikel- und Lieferantennr.' : 'Nummern und Begriffe' }}
              </span>
            </span>
          </CButton>
          <CButton
            color="light"
            size="sm"
            class="material-filter-toggle"
            :class="{ 'material-filter-toggle--active': manufacturerOnly }"
            @click="toggleManufacturerOnly"
          >
            <span class="material-filter-toggle__state" :class="{ 'material-filter-toggle__state--active': manufacturerOnly }">
              <CIcon :name="manufacturerOnly ? 'cil-check' : 'cil-x'" />
            </span>
            <span class="material-filter-toggle__text">
              <span class="material-filter-toggle__title">Nur Katalog</span>
              <span class="material-filter-toggle__hint">
                {{ manufacturerOnly ? 'Ohne Protokollmaterial' : 'Mit Protokollmaterial' }}
              </span>
            </span>
          </CButton>
        </div>
      </div>
      <ValidationProvider :rules="rules" immediate v-slot="{ errors }">
        <v-select
          v-model="filterValue"
          ref="materialCatalogSelect"
          :loading="wait"
          :dropdown-should-open="dropdownShouldOpen"
          :clear-search-on-blur="() => false"
          :options="displayOptions"
          placeholder="Bitte wählen Sie ein Material aus ..."
          :filterable="false"
          v-on:input="inputEvent"
          :class="isValidClass(errors)"
          :get-option-label="(option) => generateLabelName(option)"
          @open="handleDropdownOpen"
          @search="onSearch"
        >
          <template #option="option">
            <div :class="option.protokollid ? 'material-option material-option--historical' : 'material-option'">
              <div class="material-option__grid">
                <div
                  v-for="(part, index) in buildLabelParts(option)"
                  :key="`material-option-${option.materialid || index}-${part.key}-${index}`"
                  class="material-option__cell"
                  :class="`material-option__cell--${part.key}`"
                >
                  <div class="material-option__value">
                    <span v-if="part.bracketed">(</span><span v-html="highlightedHtml(part.value, part.mode)"></span><span v-if="part.bracketed">)</span>
                  </div>
                  <div class="material-option__label">{{ part.label }}</div>
                </div>
              </div>
            </div>
          </template>
          <template #selected-option="option">
            <div :class="option.protokollid ? 'material-option material-option--historical material-option--selected' : 'material-option material-option--selected'">
              <div class="material-option__selected-summary">
                <span
                  v-for="(part, index) in buildLabelParts(option)"
                  :key="`material-selected-${option.materialid || index}-${part.key}-${index}`"
                  class="material-option__selected-part"
                >
                  <span v-if="index > 0" class="material-option__separator"> | </span>
                  <span v-if="part.bracketed">(</span><span v-html="highlightedHtml(part.value, part.mode)"></span><span v-if="part.bracketed">)</span>
                </span>
              </div>
            </div>
          </template>
          <template #no-options>
            {{ wait ? 'Suche ...' : 'Keine Materialien gefunden.' }}
          </template>
          <template #list-footer>
            <li
              v-if="showResultLimitHint"
              class="vs__dropdown-option material-option__info"
              @mousedown.prevent
            >
              {{ searchResultHint }}
            </li>
          </template>
        </v-select>
      </ValidationProvider>
      <div class="material-recent-list">
        <div class="material-recent-list__title">Zuletzt verwendet (letzte 4 Wochen)</div>
        <div class="material-recent-list__badges">
          <button
            v-for="recentMaterial in visibleRecentMaterialSuggestions"
            :key="`recent-material-${recentMaterial.materialid}`"
            type="button"
            class="material-recent-card"
            @click.stop="selectRecentMaterial(recentMaterial)"
          >
            <div class="material-recent-card__grid">
              <div
                v-for="(part, index) in buildRecentBadgeParts(recentMaterial)"
                :key="`recent-material-part-${recentMaterial.materialid}-${part.key}-${index}`"
                class="material-recent-card__cell"
                :class="`material-recent-card__cell--${part.key}`"
              >
                <div class="material-recent-card__value">{{ part.value }}</div>
                <div class="material-recent-card__label">{{ part.label }}</div>
              </div>
            </div>
          </button>
          <div
            v-if="!wait && recentMaterialSuggestions.length === 0"
            class="material-recent-list__empty"
          >
            Keine Materialien in den letzten 4 Wochen gefunden
          </div>
          <div
            v-if="wait && recentMaterialSuggestions.length === 0"
            class="material-recent-list__empty"
          >
            Lädt ...
          </div>
        </div>
        <button
          v-if="hasCollapsedRecentMaterials"
          type="button"
          class="material-recent-list__toggle"
          @click="toggleRecentMaterialsExpanded"
        >
          <span class="material-recent-list__toggle-icon">{{ recentMaterialsExpanded ? '−' : '+' }}</span>
          <span>{{ recentMaterialsExpanded ? 'Weniger anzeigen' : `Weitere ${recentMaterialSuggestions.length - 1} anzeigen` }}</span>
        </button>
      </div>
    </CCol>
  </CRow>
</template>

<script>
import Vue from 'vue'

export default {
  name: 'MaterialKatalogAuswahl',
  props: {
    value: [Object, Event],
    formLabel: {
      default: 'Materialkatalog',
      type: String
    },
    formLabelClass: {
      default: 'd-none',
      type: String
    },
    rules: {
      default: '',
      type: String
    }
  },
  data () {
    return {
      isValid: false,
      filterValue: null,
      materialCatalog: [],
      catalogResults: [],
      manufacturerOnly: true,
      numbersOnly: false,
      offset: 0,
      limit: 10,
      recentMaterialSuggestions: [],
      recentMaterialsExpanded: false,
      wait: false,
      search: '',
      searchDebounceTimer: null,
      lastRequestId: 0,
      queuedSearchQuery: null
    }
  },
  computed: {
    paginated () {
      return Array.isArray(this.catalogResults) ? this.catalogResults.slice(0, 150) : []
    },
    displayOptions () {
      return this.paginated
    },
    visibleRecentMaterialSuggestions () {
      if (!Array.isArray(this.recentMaterialSuggestions)) {
        return []
      }

      if (this.recentMaterialsExpanded) {
        return this.recentMaterialSuggestions
      }

      return this.recentMaterialSuggestions.slice(0, 1)
    },
    hasCollapsedRecentMaterials () {
      return Array.isArray(this.recentMaterialSuggestions) && this.recentMaterialSuggestions.length > 1
    },
    showResultLimitHint () {
      return Array.isArray(this.materialCatalog) && this.materialCatalog.length >= this.limit
    },
    searchResultHint () {
      const searchDescription = this.search && this.search.trim() !== ''
        ? `Suche: "${this.search.trim()}"`
        : 'Suche: ohne Begriff'
      const dataScopeDescription = this.manufacturerOnly
        ? 'Datenbasis: nur Lieferanten-/Herstellerdaten'
        : 'Datenbasis: inkl. Protokollmaterial'
      const searchModeDescription = this.numbersOnly
        ? 'Suchmodus: nur Artikel-/Lieferantennummern'
        : 'Suchmodus: Nummern und Begriffe'

      return `Es werden nur die ersten ${this.limit} Treffer angezeigt. ${searchDescription}. ${dataScopeDescription}. ${searchModeDescription}. Bitte Suche weiter eingrenzen.`
    },
    skeletonRowCount () {
      return 6
    }
  },
  async mounted () {
    Promise.all([
      this.loadMaterialCatalog(),
      this.loadRecentMaterialSuggestions()
    ]).then(() => {
      this.valueToLocal()
    })
  },
  beforeDestroy () {
    if (this.searchDebounceTimer) {
      window.clearTimeout(this.searchDebounceTimer)
    }
  },
  watch: {
    value: function () {
      this.valueToLocal()
    },
    manufacturerOnly: function () {
      this.loadRecentMaterialSuggestions()
      this.scheduleSearch(this.search, true)
    },
    numbersOnly: function () {
      this.scheduleSearch(this.search, true)
    }
  },
  methods: {
    valueToLocal: function () {
      this.filterValue = this.value
      if (this.value) {
        this.isValid = true
      }
      const self = this
      if (this.filterValue && this.materialCatalog) {
        const arrayid = Vue._.findIndex(this.materialCatalog, function (material) { return material.materialid === self.filterValue })
        if (arrayid !== -1) {
          this.filterValue = this.materialCatalog[arrayid]
        }
      }
    },
    onSearch (query) {
      this.search = query
      this.offset = 0
      this.scheduleSearch(query)
    },
    scheduleSearch (query, immediate = false) {
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer)
      }

      this.searchDebounceTimer = window.setTimeout(() => {
        this.enqueueMaterialCatalogLoad(query)
      }, immediate ? 0 : this.getSearchDelay(query))
    },
    inputEvent: function (newValue) {
      if (newValue) {
        this.isValid = true
        this.$emit('input', newValue)
        this.loadRecentMaterialSuggestions()
        this.resetSelector(false)
      } else {
        this.$emit('input', null)
      }
    },
    isValidClass: function (errors) {
      let result = 'form-control'
      if (errors.length === 0) {
        result = result + ' is-valid'
      } else {
        result = result + ' is-invalid'
      }
      return result
    },
    generateLabelName: function (material) {
      const herstellerLabel = material.hersteller ? `(${material.hersteller})` : ''
      const historicalLabel = material.protokollid ? '*' : ''
      return [material.material, material.artikelnr, material.herstellernummer, herstellerLabel, historicalLabel].filter(Boolean).join(' | ')
    },
    buildLabelParts (material) {
      const parts = [
        { key: 'material', label: 'Material', value: material.material, mode: 'normalized', bracketed: false },
        { key: 'artikelnr', label: 'Art.-Nr.', value: material.artikelnr, mode: 'direct', bracketed: false },
        { key: 'herstellernummer', label: 'Lieferantennr.', value: material.herstellernummer || '-', mode: 'direct', bracketed: false }
      ]

      if (material.hersteller) {
        parts.push({ key: 'hersteller', label: 'Lieferant/Hersteller', value: material.hersteller, mode: 'direct', bracketed: false })
      }

      if (material.protokollid) {
        parts.push({ key: 'historical', label: 'Quelle', value: 'Protokoll *', mode: 'none', bracketed: false })
      }

      return parts
    },
    buildRecentBadgeParts (material) {
      return [
        { key: 'material', label: 'Material', value: material.material || '-' },
        { key: 'artikelnr', label: 'Art.-Nr.', value: material.artikelnr || '-' },
        { key: 'herstellernummer', label: 'Lieferantennr.', value: material.herstellernummer || '-' }
      ]
    },
    highlightedHtml (value, mode = 'direct') {
      const sourceText = String(value || '')
      const searchText = String(this.search || '').trim()

      if (sourceText === '') {
        return ''
      }

      const escapedText = this.escapeHtml(sourceText)
      if (searchText === '' || mode === 'none') {
        return escapedText
      }

      const matchRange = mode === 'normalized'
        ? this.findNormalizedMatchRange(sourceText, searchText)
        : this.findDirectMatchRange(sourceText, searchText)

      if (!matchRange) {
        return escapedText
      }

      const start = matchRange.start
      const end = matchRange.end
      const before = this.escapeHtml(sourceText.slice(0, start))
      const match = this.escapeHtml(sourceText.slice(start, end))
      const after = this.escapeHtml(sourceText.slice(end))

      return `${before}<span class="material-option__highlight">${match}</span>${after}`
    },
    findDirectMatchRange (sourceText, searchText) {
      const lowerSource = sourceText.toLocaleLowerCase()
      const lowerSearch = searchText.toLocaleLowerCase()
      const matchIndex = lowerSource.indexOf(lowerSearch)

      if (matchIndex === -1) {
        return null
      }

      return {
        start: matchIndex,
        end: matchIndex + searchText.length
      }
    },
    findNormalizedMatchRange (sourceText, searchText) {
      const normalizedSource = this.normalizeSearchText(sourceText)
      const normalizedSearch = this.normalizeSearchText(searchText)

      if (normalizedSource.normalized === '' || normalizedSearch.normalized === '') {
        return this.findDirectMatchRange(sourceText, searchText)
      }

      const normalizedMatchIndex = normalizedSource.normalized.indexOf(normalizedSearch.normalized)
      if (normalizedMatchIndex === -1) {
        return this.findDirectMatchRange(sourceText, searchText)
      }

      const startMap = normalizedSource.map[normalizedMatchIndex]
      const endMap = normalizedSource.map[normalizedMatchIndex + normalizedSearch.normalized.length - 1]
      if (startMap === undefined || endMap === undefined) {
        return this.findDirectMatchRange(sourceText, searchText)
      }

      return {
        start: startMap,
        end: endMap + 1
      }
    },
    normalizeSearchText (value) {
      const sourceText = String(value || '')
      const loweredText = sourceText.toLocaleLowerCase()
      let normalized = ''
      const map = []

      for (let index = 0; index < loweredText.length; index++) {
        const character = loweredText[index]
        if (!/[0-9a-zäöüß]/i.test(character)) {
          continue
        }

        normalized += character
        map.push(index)
      }

      return { normalized, map }
    },
    getSearchDelay (value) {
      const normalizedValue = String(value || '').trim()
      if (normalizedValue === '') {
        return 0
      }

      return normalizedValue.length % 2 === 0 ? 180 : 1000
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    sortCatalogResults (results) {
      return Array.isArray(results) ? results.slice() : []
    },
    resetSelector (focusSearch = true) {
      this.$nextTick(() => {
        this.filterValue = null
        this.search = ''
        this.loadMaterialCatalog()

        const materialCatalogSelect = this.$refs.materialCatalogSelect
        if (!materialCatalogSelect) {
          return
        }

        materialCatalogSelect.search = ''
        if (materialCatalogSelect.searchEl) {
          materialCatalogSelect.searchEl.value = ''
          if (focusSearch) {
            materialCatalogSelect.searchEl.focus()
          }
        }
      })
    },
    toggleManufacturerOnly () {
      this.manufacturerOnly = !this.manufacturerOnly
    },
    toggleNumbersOnly () {
      this.numbersOnly = !this.numbersOnly
    },
    selectRecentMaterial (material) {
      if (!material) {
        return
      }

      this.inputEvent(material)
    },
    toggleRecentMaterialsExpanded () {
      this.recentMaterialsExpanded = !this.recentMaterialsExpanded
    },
    handleDropdownOpen () {
      this.loadRecentMaterialSuggestions()
    },
    dropdownShouldOpen (vs) {
      return !vs.noDrop && vs.open
    },
    enqueueMaterialCatalogLoad (query = '') {
      const normalizedQuery = String(query || '')

      if (this.wait) {
        this.queuedSearchQuery = normalizedQuery
        return Promise.resolve()
      }

      return this.loadMaterialCatalog(normalizedQuery)
    },
    loadMaterialCatalog: function (query = '') {
      const requestId = ++this.lastRequestId
      this.wait = true
      this.queuedSearchQuery = null
      const requestParams = {
        limit: this.limit,
        include_historical: this.manufacturerOnly ? 0 : 1,
        numbers_only: this.numbersOnly ? 1 : 0
      }

      if (query && query.trim() !== '') {
        requestParams.q = query.trim()
      }

      return Vue.axios.get('/material/getall', { params: requestParams })
        .then((response) => {
          if (requestId !== this.lastRequestId) {
            return
          }

          const responseItems = Array.isArray(response.data) ? response.data : []
          this.materialCatalog = this.sortCatalogResults(responseItems)
          this.catalogResults = this.materialCatalog.length > 0
            ? this.materialCatalog
            : [{
                materialid: 'no-result',
                material: 'Keine Ergebnisse gefunden',
                artikelnr: 'Bitte versuche es erneut.',
                hersteller: '',
                stueck: '',
                historical: false,
                protokollid: null,
                qtysuggestions: []
              }]

          const materialCatalogSelect = this.$refs.materialCatalogSelect
          if (materialCatalogSelect && materialCatalogSelect.searchEl && this.search !== '') {
            materialCatalogSelect.search = this.search
            materialCatalogSelect.searchEl.value = this.search
          }
        })
        .finally(() => {
          if (requestId === this.lastRequestId) {
            this.wait = false
            if (this.queuedSearchQuery !== null && this.queuedSearchQuery !== query) {
              const queuedSearchQuery = this.queuedSearchQuery
              this.queuedSearchQuery = null
              this.enqueueMaterialCatalogLoad(queuedSearchQuery)
            }
          }
        })
    },
    loadRecentMaterialSuggestions () {
      return Vue.axios.get('/material/recent', {
        params: {
          limit: 5,
          include_historical: this.manufacturerOnly ? 0 : 1
        }
      }).then((response) => {
        this.recentMaterialSuggestions = Array.isArray(response.data) ? response.data.slice(0, 5) : []
        if (this.recentMaterialSuggestions.length <= 1) {
          this.recentMaterialsExpanded = false
        }
      }).catch(() => {
        this.recentMaterialSuggestions = []
        this.recentMaterialsExpanded = false
      })
    }
  }
}
</script>

<style lang="scss">
div.material-katalog-auswahl-formgroup {
  div.form-control {
    padding: 1px 0 0 1px;
    div.vs__dropdown-toggle {
      border: none;
    }
  }
  div.v-select {
    height: auto;
  }
}

.vs__dropdown-menu {
  max-height: 90vh;
}

.material-option {
  display: block;
  width: 100%;
}

.material-option__separator {
  color: inherit;
}

.material-option__grid {
  display: grid;
  gap: 0.5rem 0.75rem;
  grid-template-columns: minmax(0, 2.4fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(140px, 1.2fr);
  width: 100%;
}

.material-option__cell {
  min-width: 0;
}

.material-option__value {
  color: #212529;
  font-size: 0.92rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-option__label {
  color: #8a93a2;
  font-size: 0.7rem;
  line-height: 1.1;
  margin-top: 0.15rem;
  text-transform: none;
}

.material-option__selected-summary {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-option__selected-part {
  display: inline;
}

.material-option__highlight {
  background: #fff3bf;
  border-radius: 0.15rem;
  color: #2f2f2f;
  padding: 0 0.05rem;
}

.material-option--historical {
  color: #6c757d;
}

.material-option--historical .material-option__value {
  color: #6c757d;
}

.material-option--historical .material-option__label {
  color: #9aa1ab;
}

.material-option--historical .material-option__highlight {
  background: #e6e8eb;
  color: #495057;
}

.vs__dropdown-option--highlight .material-option__highlight,
.vs__dropdown-option--selected .material-option__highlight {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.vs__dropdown-option--highlight .material-option__value,
.vs__dropdown-option--selected .material-option__value,
.vs__dropdown-option--highlight .material-option__label,
.vs__dropdown-option--selected .material-option__label {
  color: #fff;
}

.vs__dropdown-option--highlight .material-option--historical .material-option__highlight,
.vs__dropdown-option--selected .material-option--historical .material-option__highlight {
  background: rgba(255, 255, 255, 0.35);
  color: #fff;
}

@media (max-width: 991.98px) {
  .material-option__grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .material-katalog-auswahl-formgroup .justify-content-end {
    width: 100%;
    margin-top: 0.5rem;
  }

  .material-filter-toggle {
    margin-right: 0 !important;
    margin-top: 0.5rem;
    width: 100%;
  }
}

.material-filter-toggle {
  border: 1px solid #d8dbe0;
  color: #3c4b64;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  padding: 0.4rem 0.75rem;
  min-height: 2.5rem;
  text-align: left;
}

.material-filter-toggle--active {
  background: #e8f4ee;
  border-color: #4dbd74;
  color: #2f6b43;
  box-shadow: inset 0 0 0 1px rgba(77, 189, 116, 0.2);
}

.material-filter-toggle__state {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 1.5rem;
  background: #e9ecef;
  color: #6c757d;
  border: 1px solid #ced2d8;
}

.material-filter-toggle__state--active {
  background: #4dbd74;
  border-color: #4dbd74;
  color: #fff;
}

.material-filter-toggle__text {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.material-filter-toggle__title {
  font-size: 0.875rem;
}

.material-filter-toggle__hint {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6c757d;
}

.material-filter-toggle--active .material-filter-toggle__hint {
  color: #2f6b43;
}

.material-option__info {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  color: #6c757d;
  cursor: default;
  font-size: 0.8125rem;
  line-height: 1.35;
  padding: 0.75rem 1rem;
  white-space: normal;
}

.material-recent-list {
  margin-top: 0.65rem;
}

.material-recent-list__title {
  color: #6c757d;
  font-size: 0.72rem;
  margin-bottom: 0.4rem;
}

.material-recent-list__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.material-recent-card {
  background: #f8f9fa;
  border: 1px solid #d8dbe0;
  border-radius: 0.5rem;
  color: #212529;
  cursor: pointer;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  text-align: left;
  width: 100%;
}

.material-recent-card:hover {
  background: #eef1f4;
  border-color: #c8ccd2;
}

.material-recent-card__grid {
  display: grid;
  gap: 0.35rem 0.55rem;
  grid-template-columns: minmax(0, 2fr) minmax(70px, 0.8fr) minmax(90px, 0.9fr);
}

.material-recent-card__cell {
  min-width: 0;
}

.material-recent-card__value {
  color: #212529;
  font-size: 0.82rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-recent-card__label {
  color: #8a93a2;
  font-size: 0.64rem;
  line-height: 1.05;
  margin-top: 0.08rem;
}

.material-recent-list__empty {
  color: #6c757d;
  font-size: 0.82rem;
  padding: 0.25rem 0;
}

.material-recent-list__toggle {
  appearance: none;
  background: #f5f6f8;
  border: 1px solid #d8dbe0;
  border-radius: 999px;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.82rem;
  font-weight: 500;
  margin-top: 0.45rem;
  min-height: 2.2rem;
  padding: 0.45rem 0.8rem;
}

.material-recent-list__toggle:hover {
  color: #000;
  background: #eceef1;
  border-color: #c5c9d0;
}

.material-recent-list__toggle:focus {
  outline: none;
}

.material-recent-list__toggle-icon {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1;
}

@media (max-width: 991.98px) {
  .material-recent-list__badges {
    display: block;
  }

  .material-recent-card + .material-recent-card {
    margin-top: 0.5rem;
  }

  .material-recent-card__grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}
</style>
